<script setup>
import flatpickr from "flatpickr";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { onMounted, ref, watch } from "vue";
import { useRouter } from 'vue-router';

const router = useRouter();
const datePickerEl = ref(null);
// Create ref to store the flatpickr instance
const datePicker = ref(null);
// Add a loading state ref
const isLoading = ref(true);
// Ref for status select element
const statusSelectEl = ref(null);

const shipments = ref([]);

onMounted(() => {
	// Check authentication before loading the page
	const token = localStorage.getItem('auth_token');
	const expiresAt = localStorage.getItem('expiresAt');

	console.log({
		token,
		expiresAt,
		ee: new Date(expiresAt).getTime() <= new Date().getTime()
	})

	// If no token or token is expired, redirect to login
	if (!token || !expiresAt || new Date(expiresAt).getTime() <= new Date().getTime()) {
		localStorage.clear();
		router.push('/login');
		return;
	}

	// Authentication is valid, set loading to false
	isLoading.value = false;

});

watch(() => !isLoading.value && datePickerEl.value, (newValue) => {
	console.log(newValue);
	if (newValue) {
		datePicker.value = flatpickr(datePickerEl.value, {
			altInput: true,
			altFormat: "F j, Y",
			dateFormat: "Y-m-d",
			onChange: (_, dateStr) => {
				console.log("Selected date:", dateStr);
			}
		});


		fetch(`${import.meta.env['PUBLIC_API_URL']}/shipments`, {
			method: 'GET',
			headers: {
				'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
			}
		}).then((response) => response.json())
			.then((res) => {
				shipments.value = res.shipments || [];
				return res;
			}).catch(_ => {
				console.error("Error", "We ran into a problem while fetching the data for this user.");
			})
	}
});




</script>

<script>

const INPUT_CLASS =
	"rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium placeholder:text-[85%] placeholder:text-gray-400 disabled:bg-gray-200 disabled:text-gray-300";

export default {
	mounted() {
		// When the component is mounted, set up click handlers for date picker and status select
		this.$nextTick(() => {
			// Handle date picker icon click
			const datePickerIcon = document.getElementById("csdsvg");
			if (datePickerIcon) {
				datePickerIcon.addEventListener("click", () => {
					document.getElementById("csdsvg").parentElement.lastElementChild.click()
				});
			}

			// Handle status select dropdown icon click
			const statusDiv = document.getElementById("status-div");
			const statusSvg = document.getElementById("status-svg");
			
			[statusDiv, statusSvg].forEach(item => {
				if (item) {
					item.addEventListener("click", () => {
						const statusEl = document.getElementById("status");
						if (statusEl && "showPicker" in HTMLSelectElement.prototype) {
							statusEl.showPicker();
						}
					});
				}
			});
		});
	}
}

</script>

<template>
	<!-- Loading Spinner while checking authentication -->
	<div v-if="isLoading" class="w-full h-screen overflow-hidden flex items-center justify-center bg-page">
		<div class="flex flex-col items-center">
			<div class="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
			<p class="mt-4 text-primary font-monster font-medium">Authenticating...</p>
		</div>
	</div>

	<!-- Main content - only shown when authenticated -->
	<div v-else class="w-[calc(100vw-5.5rem)] max-w-full flex-grow flex flex-col pr-10 gap-y-8">
		<div class="flex flex-row !h-[25%] bg-page rounded-[1.25rem] gap-x-8 *:rounded-[1.25rem] *:flex">
			<div class="w-full bg-page flex flex-col gap-4">
				<div class="bg-page -mt-2 flex flex-col">

					<div class="flex flex-col gap-2 w-full flex-grow py-3">
						<div class="flex flex-row w-full gap-4">
							<div class="flex flex-col w-full">
								<label class="font-normal font-monster text-secondary" for="gbl">
									GBL or Registration #
								</label>
								<input id="gbl" name="gbl"
									:class="twMerge(INPUT_CLASS, 'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]', clsx({ 'error': false }))" />
							</div>
							<div class="flex flex-col w-full">
								<label class="font-normal font-monster text-secondary" for="name">
									Shipper First or Last Name
								</label>
								<input id="name" name="name"
									:class="twMerge(INPUT_CLASS, 'shadow-[2px_2px_6px_rgba(187,195,206,0.4)]', clsx({ 'error': false }))" />
							</div>
						</div>
						<div class="flex flex-row w-full gap-4">
							<div class="flex flex-col w-full relative *:cursor-pointer">
								<label class="font-normal font-monster text-secondary" for="csd">
									Current Status Date
								</label>
								<svg class="absolute right-4 top-[50%]" width="22" height="22" viewBox="0 0 22 22"
									fill="none" xmlns="http://www.w3.org/2000/svg" id="csdsvg">
									<g clip-path="url(#clip0_541_282)">
										<path
											d="M16.5 4.58337H5.50002C4.4875 4.58337 3.66669 5.40419 3.66669 6.41671V17.4167C3.66669 18.4292 4.4875 19.25 5.50002 19.25H16.5C17.5125 19.25 18.3334 18.4292 18.3334 17.4167V6.41671C18.3334 5.40419 17.5125 4.58337 16.5 4.58337Z"
											stroke="#020754" stroke-width="2" stroke-linecap="round"
											stroke-linejoin="round" />
										<path d="M14.6667 2.75V6.41667" stroke="#020754" stroke-width="2"
											stroke-linecap="round" stroke-linejoin="round" />
										<path d="M7.33331 2.75V6.41667" stroke="#020754" stroke-width="2"
											stroke-linecap="round" stroke-linejoin="round" />
										<path d="M3.66669 10.0834H18.3334" stroke="#020754" stroke-width="2"
											stroke-linecap="round" stroke-linejoin="round" />
										<path d="M10.0833 13.75H11" stroke="#020754" stroke-width="2"
											stroke-linecap="round" stroke-linejoin="round" />
										<path d="M11 13.75V16.5" stroke="#020754" stroke-width="2"
											stroke-linecap="round" stroke-linejoin="round" />
									</g>
									<defs>
										<clipPath id="clip0_541_282">
											<rect width="22" height="22" fill="white" />
										</clipPath>
									</defs>
								</svg>

								<input id="csd" name="csd" ref="datePickerEl"
									:class="twMerge(INPUT_CLASS, 'shadow-[2px_2px_6px_rgba(187,195,206,0.4)] cursor-pointer', clsx({ 'error': false }))" />
							</div>
							<div class="flex flex-col w-full relative">
								<label class="font-normal font-monster text-secondary" for="status">Current Status</label>
								<select id="status" name="status" ref="statusSelectEl"
									:class="twMerge(INPUT_CLASS, 'peer w-full shadow-[2px_2px_6px_rgba(187,195,206,0.4)] ', clsx({ 'error': false }))"
									style="appearance: none;text-indent: 1px;text-overflow: ''">
									<option value="" class="text-gray-400"></option>
									<option value="created">Created</option>
									<option value="inprocess">InProcess</option>
									<option value="intransit">InTransit</option>
									<option value="arrived">Arrived</option>
									<option value="stowed">Stowed</option>
								</select>
								<div id="status-div"
									class="absolute z-[1] right-0 top-[15px] h-full px-4 flex items-center cursor-pointer">
									<svg id="status-svg" width="23" height="13" viewBox="0 0 23 13" fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<path fill-rule="evenodd" clip-rule="evenodd"
											d="M2.71933 0.466564C2.09725 -0.155521 1.08865 -0.155521 0.466564 0.466564C-0.155521 1.08865 -0.155521 2.09725 0.466564 2.71933L10.0243 12.277C10.6463 12.8991 11.6549 12.8991 12.277 12.277L21.8347 2.71933C22.4568 2.09725 22.4568 1.08865 21.8347 0.466564C21.2126 -0.155521 20.204 -0.155521 19.5819 0.466564L11.1506 8.89787L2.71933 0.466564Z"
											fill="#020754" />
									</svg>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			class="bg-white -mt-4 flex flex-col w-full max-w-full border-solid border-[1px] rounded-[14px] border-[#EDF2F7] px-5 h-[100vh] flex-[1_1_auto] overflow-y-scroll relative overflow-x-hidden">
			<div className="flex flex-col gap-3 justify-center pt-4 bg-white sticky top-0 w-full">
				<h6 className="font-monster font-bold leading-none text-secondary">Shipment List</h6>
				<div class="w-full overflow-x-auto h-scrollbar">
					<table class="min-w-full table-fixed whitespace-nowrap">
						<thead class="sticky top-0 bg-white">
							<tr class="font-monster font-bold text-secondary *:text-[85%]">
								<th class="py-2 text-left min-w-[100px]">Printed</th>
								<th class="py-2 text-left min-w-[200px]">Coleman Registration #</th>
								<th class="py-2 text-left min-w-[200px]">GBL / Other Registration</th>
								<th class="py-2 text-left min-w-[150px]">Shipper Last Name</th>
								<th class="py-2 text-left min-w-[150px]">Shipper First Name</th>
								<th class="py-2 text-left min-w-[150px]">Current Status</th>
								<th class="py-2 text-left min-w-[120px]">Status Date</th>
								<th class="py-2 text-left min-w-[150px]">Active Terminal</th>
								<th class="py-2 text-left min-w-[120px]">Origin Port</th>
								<th class="py-2 text-left min-w-[120px]">Dest. Port</th>
								<th class="py-2 text-left min-w-[80px]">QTY</th>
								<th class="py-2 text-left min-w-[100px]">TTL WGT</th>
								<th class="py-2 text-left min-w-[100px]">TTL CUBES</th>
								<th class="py-2 text-left min-w-[180px]">Shipment Category</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(shipment, index) in shipments" :key="index"
								class="font-monster text-black border-t border-gray-200 *:text-[80%] hover:bg-gray-50 cursor-pointer"
								@click="router.push(`/${shipment.type || 'unknown'}-shipment/${shipment.id}`)">
								<td class="py-2">{{ shipment.printed ? 'Yes' : 'No' }}</td>
								<td class="py-2">{{ shipment?.proNumber || "Unassigned" }}</td>
								<td class="py-2">{{ shipment.shipment_type_info?.gbl ||
									shipment.shipment_type_info?.registrationNumber || "-" }}</td>
								<td class="py-2">{{ shipment.shipper_info?.shipper?.lastName || "-" }}</td>
								<td class="py-2">{{ shipment.shipper_info?.shipper?.firstName || "-" }}</td>
								<td class="py-2">{{ shipment.status === "completed" ? "Created" : "-" }}</td>
								<td class="py-2">{{ new Intl.DateTimeFormat('en-US').format(new
									Date(shipment.created_at)) }}</td>
								<td class="py-2">{{ shipment.originTerminal || "-" }}</td>
								<td class="py-2">{{ shipment.shipper_info?.origin?.portAgent || "-" }}</td>
								<td class="py-2">{{ shipment.shipper_info?.destination?.portAgent || "-" }}</td>
								<td class="py-2">{{ Array.isArray(shipment.line_items) ?
									Array.from(shipment.line_items).length.toString() : "0" }}</td>
								<td class="py-2">{{Array.isArray(shipment.line_items) ?
									shipment.line_items.reduce((total, item) =>
										total + (Number(item?.grossWeight) || 0), 0) : "-"}}</td>
								<td class="py-2">{{Array.isArray(shipment.line_items) ?
									shipment.line_items.reduce((total, item) =>
										total + (Number(item?.cubes) || 0), 0) : "-"}}</td>
								<td class="py-2">
									{{
										typeof shipment.shipment_type_info?.shipmentType === "string" ? (
										
										shipment.shipment_type_info.shipmentType?.split("_").join(" ")

										
									) : "-" }}
								</td>
							</tr>
							<tr v-if="shipments.length === 0">
								<td colspan="14" class="py-4 text-center text-gray-500">No shipments found</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>
